import { FloatingFormItem } from '@/components/_common';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import {
  AdministrativeReportCode,
  CompanyReportFormValues,
  DefaultName,
  ReportTypes,
} from '@/core/domain/reports/company/Company.types';
import { useWindowSize } from '@/hooks';
import {
  Card,
  Col,
  Form,
  FormInstance,
  Radio,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { Info } from 'react-feather';
import { useIntl } from 'react-intl';

interface Props {
  form: FormInstance<CompanyReportFormValues>;
}

const CompaniesReportForm = ({ form }: Props) => {
  const intl = useIntl().formatMessage;
  const { Title } = Typography;
  const { isMobile } = useWindowSize();

  const options = [
    {
      value: ReportTypes.a4,
      label: intl({ id: 'companies.Report.form.administrativeReport.a4' }),
    },
    {
      value: ReportTypes.continuousForm,
      label: intl({
        id: 'companies.Report.form.administrativeReport.continuousForm',
      }),
    },
    {
      value: ReportTypes.thermal,
      label: intl({ id: 'companies.Report.form.administrativeReport.thermal' }),
    },
    {
      value: ReportTypes.thermal58mm,
      label: intl({
        id: 'companies.Report.form.administrativeReport.58.thermal',
      }),
    },
  ];

  const administrativeReportCodeOptions = [
    {
      label: 'Código do fabricante',
      value: AdministrativeReportCode.manufacturerCode,
    },
    {
      label: 'Código interno',
      value: AdministrativeReportCode.internalCode,
    },
    {
      label: 'Código de barras',
      value: AdministrativeReportCode.barCode,
    },
    {
      label: 'Código da empresa',
      value: AdministrativeReportCode.companyCode,
    },
  ];

  const reports = [
    'Declaração de assinatura da OS',
    'Declaração de permanência',
    'Ficha de Inspeção Técnica',
    'Folha do Mecânico',
    'Mapa de cotação',
    'Relatório Administrativo',
    'Relatório Administrativo - Sintético',
    'Relatório Checklist',
    'Relatório de contas a pagar',
    'Relatório de contas a receber',
    'Relatório para Oficina',
    'Termo de Garantia',
    'Termo de Peças fornecidas',
    'Termo de Responsabilidade',
    'Termo LGPD',
  ];

  return (
    <Form form={form} name="companyReport">
      <Row gutter={isMobile ? [0, 24] : [24, 24]}>
        <Col span={24}>
          <Card title="Relatório Administrativo" type="inner">
            <Row gutter={[24, 12]}>
              <Col span={24} md={12}>
                <FloatingFormItem
                  name="administrativeReportFormat"
                  label="Formato"
                >
                  <Select options={options} />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12}>
                <FloatingFormItem
                  name="administrativeReportCode"
                  label="Código padrão a ser utilizado no relatório"
                >
                  <Select options={administrativeReportCodeOptions} />
                </FloatingFormItem>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={24} md={12}>
          <Card title="Relatório Oficina" type="inner">
            <FloatingFormItem name="workshopReportFormat" label="Formato">
              <Select
                options={options.filter(
                  (item) => item.value !== ReportTypes.thermal58mm,
                )}
              />
            </FloatingFormItem>
          </Card>
        </Col>

        <Col span={24} md={12}>
          <Card title="Relatório Mapa de cotação" type="inner">
            <FloatingFormItem name="quotationMapReportFormat" label="Formato">
              <Select options={options.slice(0, 2)} />
            </FloatingFormItem>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <Space>
                Outros
                <Tooltip
                  placement="right"
                  overlayStyle={{ maxWidth: '700px' }}
                  title={reports.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                >
                  <AntdFeatherIcon size={20} icon={<Info />} />
                </Tooltip>
              </Space>
            }
            type="inner"
          >
            <Row gutter={[24, 12]}>
              <Col span={24} md={8}>
                <Title level={5}>
                  {intl({
                    id: 'companies.Report.form.radio.title',
                  })}
                </Title>

                <FloatingFormItem name="companyNaming">
                  <Radio.Group
                    buttonStyle="solid"
                    optionType="button"
                    size="large"
                  >
                    <Radio value={DefaultName.corporateReason}>
                      {intl({
                        id: 'companies.Report.form.radio.corporateReason',
                      })}
                    </Radio>
                    <Radio value={DefaultName.fantasyName}>
                      {intl({
                        id: 'companies.Report.form.radio.fantasyName',
                      })}
                    </Radio>
                  </Radio.Group>
                </FloatingFormItem>
              </Col>

              <Col span={24} md={8}>
                <Title level={5}>
                  {intl({
                    id: 'companies.Report.form.check.DisplayNoteReport',
                  })}
                </Title>

                <FloatingFormItem name="showObservation">
                  <Radio.Group
                    buttonStyle="solid"
                    optionType="button"
                    size="large"
                  >
                    <Radio value={true}>Sim</Radio>
                    <Radio value={false}>Não</Radio>
                  </Radio.Group>
                </FloatingFormItem>
              </Col>

              <Col span={24} md={8}>
                <Title level={5}>
                  {intl({
                    id: 'companies.Report.form.check.ShowEngine',
                  })}
                </Title>

                <FloatingFormItem name="showEngine">
                  <Radio.Group
                    buttonStyle="solid"
                    optionType="button"
                    size="large"
                  >
                    <Radio value={true}>Sim</Radio>
                    <Radio value={false}>Não</Radio>
                  </Radio.Group>
                </FloatingFormItem>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Form.Item hidden noStyle name="id" />
    </Form>
  );
};

export default CompaniesReportForm;
